import state from './apps-state.js';
import * as nav from './apps-navigation.js';
import * as apps from './apps.js';
import { calculateCompleteLayout, calculateFolderDimensions } from './apps-layout.js';
window.calculateCompleteLayout = calculateCompleteLayout;
window.calculateFolderDimensions = calculateFolderDimensions;

// Helper functions for visual position calculations
export function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

export function calculateVisualPosition(region, position, layout) {
    const regionLeaf = Math.floor(region / layout.bands);
    const regionBand = region % layout.bands;    
    const posRank = Math.floor(position / 4);
    const posFile = position % 4;

    return {
        rank: regionBand * 3 + posRank,
        file: regionLeaf * 4 + posFile
    };
}

export function visualPositionToRegionPosition(rank, file, layout) {
    const totalFiles = layout.leaves * 4;
    
    if (file < 0 || file >= totalFiles) {
        return null; // Out of bounds
    }
    
    const regionBand = Math.floor(rank / 3);
    const regionLeaf = Math.floor(file / 4);
    const region = regionLeaf * layout.bands + regionBand;
    
    const posRank = rank % 3;
    const posFile = file % 4;
    const position = posRank * 4 + posFile;
    
    return { region, position };
}

function isEmptyScreenForDeletion(screenIndex, totalScreens, icons, isFolder) {
    if (screenIndex === totalScreens - 1) {
        return false; // Last screen - auto-deleted elsewhere
    }

    const layout = calculateLayout();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = screenIndex * regionsPerScreen;
    const screenEndRegion = screenStartRegion + regionsPerScreen - 1;

    const iconsInScreen = icons.filter(icon => {
        return icon.region >= screenStartRegion && icon.region <= screenEndRegion;
    });

    return iconsInScreen.length === 0;
}

// Rendering
export function renderLaunchpad() {
    state.clearAllTimers();
    
    updateCSSVariables();
    
    const rootIcons = window.icons.filter(icon => icon.kfolder === 0);
    const screens = organizeIntoScreens(rootIcons, false);
    
    // Clean up the container by cloning it (removes all event listeners)
    const screensContainer = document.getElementById('appspaceScreens');
    const newScreensContainer = screensContainer.cloneNode(false);
    newScreensContainer.id = 'appspaceScreens'; // Make sure ID is preserved
    newScreensContainer.classList.remove('dragging'); // Make sure transitions work
    newScreensContainer.style.transition = 'transform 0.3s ease-out';
    screensContainer.parentNode.replaceChild(newScreensContainer, screensContainer);
    
    // Now render into the clean container
    newScreensContainer.innerHTML = '';
    
    screens.forEach((screen, screenIndex) => {
        const screenDiv = document.createElement('div');
        screenDiv.className = 'appspace-screen';

        const isEmpty = isEmptyScreenForDeletion(screenIndex, screens.length, rootIcons, false);

        if (isEmpty) {
            screenDiv.innerHTML = renderEmptyScreenIndicator(screenIndex, false);
        } else {
            screenDiv.innerHTML = renderScreen(screen, false);
        }

        newScreensContainer.appendChild(screenDiv);
    });

    screens.forEach((screen, screenIndex) => {
        const screenDiv = document.createElement('div');
        screenDiv.className = 'appspace-screen';

        // Check if this is an empty screen that should show delete option
        const isEmpty = isEmptyScreenForDeletion(screenIndex, screens.length, rootIcons, false);

        if (isEmpty) {
            screenDiv.innerHTML = renderEmptyScreenIndicator(screenIndex, false);
        } else {
            screenDiv.innerHTML = renderScreen(screen, false);
        }

        screensContainer.appendChild(screenDiv);
    });

    renderPageIndicators(screens.length, 'pageIndicators');
    nav.goToScreen(Math.min(state.currentScreen, screens.length - 1));
    
    apps.attachEventListeners();
    
    // Attach delete handlers to empty screen indicators
    attachEmptyScreenDeleteHandlers();
}

// Navigate to first non-empty screen (call only on initial load)
export function navigateToFirstNonEmpty() {
    const rootIcons = window.icons.filter(icon => icon.kfolder === 0);
    const screens = organizeIntoScreens(rootIcons, false);
    const firstNonEmptyScreen = findFirstNonEmptyScreen(screens.length, rootIcons, false);
    nav.goToScreen(firstNonEmptyScreen);
}

// NEW: Find the first non-empty screen
function findFirstNonEmptyScreen(totalScreens, icons, isFolder) {
    const layout = calculateLayout();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    
    for (let screenIndex = 0; screenIndex < totalScreens; screenIndex++) {
        const screenStartRegion = screenIndex * regionsPerScreen;
        const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
        
        const iconsInScreen = icons.filter(icon => 
            icon.region >= screenStartRegion && icon.region <= screenEndRegion
        );
        
        if (iconsInScreen.length > 0) {
            return screenIndex;
        }
    }
    
    return 0; // Default to first screen if all empty
}

function renderEmptyScreenIndicator(screenIndex, isFolder) {
    const layout = calculateLayout();

    // Get computed gap values
    const computedIconSize = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--icon-size')) || state.config.iconSize;
    const computedGapH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')) || state.config.gapHorizontal;
    const computedGapV = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-vertical')) || state.config.gapVertical;

    // Use layout icon size for the delete icon (matches actual icons on screen)
    const iconSize = layout.iconSize;

    // Calculate the screen's region range
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = screenIndex * regionsPerScreen;
    const margin = layout.margin || 0;

    // Build overlay style with proper centering
    let overlayClass = '';
    let overlayStyle = '';
    
    if (isFolder && state.openFolder) {
        const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
        let maxGrandRank = 0;
        folderIcons.forEach(icon => {
            const grandRank = Math.floor(icon.position / 4);
            if (grandRank > maxGrandRank) maxGrandRank = grandRank;
        });

        // Calculate content dimensions
        const usedGrandRanks = maxGrandRank + 1;
        const folderGapV = Math.floor(computedIconSize * 0.15);
        const contentHeight = (computedIconSize + 20) * usedGrandRanks + (folderGapV * (usedGrandRanks - 1));
        const contentWidth = (layout.leaves * layout.regionWidth) + ((layout.leaves - 1) * layout.regionGapH);
        
        // Center within content area
        const leftOffset = margin + (contentWidth / 2);
        const topOffset = margin + (contentHeight / 2);
        
        overlayClass = ' positioned';
        overlayStyle = ` style="left: ${leftOffset}px; top: ${topOffset}px; transform: translate(-50%, -50%);"`;
    } else {
        // Main screen - center within content area
        const contentWidth = (layout.leaves * layout.regionWidth) + ((layout.leaves - 1) * layout.regionGapH);
        const contentHeight = (layout.bands * layout.regionHeight) + ((layout.bands - 1) * layout.regionGapV);
        
        const leftOffset = margin + (contentWidth / 2);
        const topOffset = margin + (contentHeight / 2);
        
        overlayStyle = ` style="left: ${leftOffset}px; top: ${topOffset}px; transform: translate(-50%, -50%);"`;
    }

    // Build the actual drop target regions
    let regionsHTML = '';

    if (isFolder) {
        // Folders: single band, with margin to match main screen
        const folderGapV = Math.floor(computedIconSize * 0.15); // Match folder gap
        const folderRegionHeight = (3 * (computedIconSize + 20)) + (2 * folderGapV);

        const regions = [];
        for (let i = 0; i < regionsPerScreen; i++) {
            regions.push({ regionNum: screenStartRegion + i, icons: [] });
        }

        regionsHTML = `
            <div class="regions-container folder-regions" style="justify-content: start; align-items: start; grid-template-columns: repeat(${layout.leaves}, ${layout.regionWidth}px); grid-template-rows: ${folderRegionHeight}px; gap: ${folderGapV}px ${layout.gapH}px; margin: ${margin}px;">
                ${regions.map(region => renderRegion(region)).join('')}
            </div>
        `;
    } else {
        // Main screen: leaf-major (region column-major) order with margin
        const regionGapH = computedGapH * 2;
        const regionGapV = computedGapV * 3;

        const regions = [];
        for (let i = 0; i < regionsPerScreen; i++) {
            regions.push({ regionNum: screenStartRegion + i, icons: [] });
        }

        // Reorder regions for visual display
        const orderedRegions = [];
        for (let band = 0; band < layout.bands; band++) {
            for (let leaf = 0; leaf < layout.leaves; leaf++) {
                const regionNum = screenStartRegion + leaf * layout.bands + band;
                orderedRegions.push({ regionNum, icons: [] });
            }
        }

        regionsHTML = `
            <div class="regions-container" style="justify-content: start; align-items: start; grid-template-columns: repeat(${layout.leaves}, ${layout.regionWidth}px); grid-template-rows: repeat(${layout.bands}, ${layout.regionHeight}px); gap: ${regionGapV}px ${regionGapH}px; margin: ${margin}px;">
                ${orderedRegions.map(region => renderRegion(region)).join('')}
            </div>
        `;
    }

    const result = `
        <div class="empty-screen-wrapper">
            ${regionsHTML}
            <div class="delete-screen-indicator-overlay${overlayClass}"${overlayStyle}>
                <div class="delete-screen-icon-button" data-screen-index="${screenIndex}" data-is-folder="${isFolder}">
                    <svg width="${iconSize}" height="${iconSize}" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="47" fill="rgba(0, 0, 0, 0.2)" stroke="rgba(255, 255, 255, 0.7)" stroke-width="6"/>
                        <line x1="30" y1="30" x2="70" y2="70" stroke="rgba(128, 128, 128, 1)" stroke-width="5" stroke-linecap="round"/>
                        <line x1="70" y1="30" x2="30" y2="70" stroke="rgba(128, 128, 128, 1)" stroke-width="5" stroke-linecap="round"/>
                    </svg>
                </div>
            </div>
        </div>
    `;
    
    return result;
}

// Attach click handlers to empty screen delete icons
function attachEmptyScreenDeleteHandlers() {
    const buttons = document.querySelectorAll('.delete-screen-icon-button');
    
    buttons.forEach(button => {
        let mouseDownOnButton = false;
        
        button.addEventListener('mousedown', (e) => {
            e.stopPropagation();
            mouseDownOnButton = true;
        });
        
        button.addEventListener('touchstart', (e) => {
            e.stopPropagation();
            mouseDownOnButton = true;
        }, { passive: true });
        
        button.addEventListener('mouseup', async (e) => {
            e.stopPropagation();
            if (mouseDownOnButton) {
                const screenIndex = parseInt(button.dataset.screenIndex);
                const isFolder = button.dataset.isFolder === 'true';
                
                // Call the delete function
                await nav.deleteEmptyScreen(screenIndex, isFolder);
            }
            mouseDownOnButton = false;
        });
        
        button.addEventListener('touchend', async (e) => {
            e.stopPropagation();
            if (mouseDownOnButton) {
                const screenIndex = parseInt(button.dataset.screenIndex);
                const isFolder = button.dataset.isFolder === 'true';
                
                // Call the delete function
                await nav.deleteEmptyScreen(screenIndex, isFolder);
            }
            mouseDownOnButton = false;
        });
        
        button.addEventListener('mouseleave', () => {
            mouseDownOnButton = false;
        });
        
        // Prevent the button from interfering with drag operations
        button.addEventListener('dragstart', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
    });
}

export function updateCSSVariables() {
    const viewport = document.getElementById('appspaceViewport');
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;
    
    // Use new layout calculation system
    const layout = calculateCompleteLayout(viewportWidth, viewportHeight);
    
    // Store layout for other modules to use
    state.currentLayout = layout;
    
    // Set CSS variables
    document.documentElement.style.setProperty('--icon-size', `${layout.iconSize}px`);
    document.documentElement.style.setProperty('--icon-gap-horizontal', `${layout.gapH}px`);
    document.documentElement.style.setProperty('--icon-gap-vertical', `${layout.gapV}px`);
    document.documentElement.style.setProperty('--title-max-width', `${layout.titleMaxWidth}px`);
    document.documentElement.style.setProperty('--outer-margin', `${layout.margin}px`);
    document.documentElement.style.setProperty('--region-gap-horizontal', `${layout.regionGapH}px`);
    document.documentElement.style.setProperty('--region-gap-vertical', `${layout.regionGapV}px`);
}

export function calculateLayout() {
    // Use stored layout from updateCSSVariables, or calculate fresh
    if (state.currentLayout) {
        return state.currentLayout;
    }
    
    const viewport = document.getElementById('appspaceViewport');
    const layout = calculateCompleteLayout(viewport.clientWidth, viewport.clientHeight);
    state.currentLayout = layout;
    return layout;
}

export function organizeIntoScreens(iconList, isFolder = false) {
    // Filter out negative regions
    const validIcons = iconList.filter(icon => icon.region >= 0);

    const layout = calculateLayout();
    
    if (isFolder) {
        // Folders always have single band, numbered [0,1,2,3,...]
        const regionsPerScreen = layout.leaves;
        const maxRegion = Math.max(...validIcons.map(icon => icon.region), -1);
        const screens = [];
        
        for (let screenStart = 0; screenStart <= maxRegion; screenStart += regionsPerScreen) {
            const screenRegions = [];
            // Always create all regions for the current screen to allow dropping
            for (let i = 0; i < regionsPerScreen; i++) {
                const regionNum = screenStart + i;
                const regionIcons = validIcons.filter(icon => icon.region === regionNum);
                screenRegions.push({ regionNum, icons: regionIcons });
            }
            screens.push(screenRegions);
        }
        
        // If no screens at all (empty folder), create at least one screen with all regions
        if (screens.length === 0) {
            const emptyScreen = [];
            for (let i = 0; i < regionsPerScreen; i++) {
                emptyScreen.push({ regionNum: i, icons: [] });
            }
            screens.push(emptyScreen);
        }
        
        return screens;
    } else {
        // Main screen uses leaf-major (region column-major) ordering
        const regionsPerScreen = layout.leaves * layout.bands;
        const maxRegion = Math.max(...validIcons.map(icon => icon.region), -1);
        const screens = [];
        
        for (let screenStart = 0; screenStart <= maxRegion; screenStart += regionsPerScreen) {
            const screenRegions = [];
            for (let i = 0; i < regionsPerScreen; i++) {
                const regionNum = screenStart + i;
                const regionIcons = validIcons.filter(icon => icon.region === regionNum);
                if (regionNum <= maxRegion || regionIcons.length > 0) {
                    screenRegions.push({ regionNum, icons: regionIcons });
                }
            }
            if (screenRegions.length > 0) {
                screens.push(screenRegions);
            }
        }
        
        return screens.length > 0 ? screens : [[{ regionNum: 0, icons: [] }]];
    }
}

export function renderScreen(regions, isFolder = false) {
    const layout = calculateLayout();
    
    if (isFolder) {
        // Folders: single band, with compact vertical spacing
        const margin = layout.margin || 0;
        
        // Calculate folder-specific vertical gap
        const folderGapV = Math.floor(layout.iconSize * 0.15); // Match LAYOUT_CONFIG.folderGapVerticalRatio
        
        // Set CSS variable for folder vertical gap
        document.documentElement.style.setProperty('--folder-icon-gap-vertical', `${folderGapV}px`);
        
        // Recalculate region height with folder gap
        const textHeight = 20;
        const folderRegionHeight = (3 * (layout.iconSize + textHeight)) + (2 * folderGapV);
        
        return `
            <div class="regions-container folder-regions" style="
                justify-content: start; 
                align-items: start; 
                grid-template-columns: repeat(${layout.leaves}, ${layout.regionWidth}px); 
                grid-template-rows: ${folderRegionHeight}px; 
                gap: ${layout.regionGapV}px ${layout.regionGapH}px;
                margin: ${margin}px;
            ">
                ${regions.map(region => renderRegion(region)).join('')}
            </div>
        `;
    } else {
        // Main screen: leaf-major (region column-major) order with flexible gaps
        const margin = layout.margin || 0;
        
        console.log("REGIONS");
        console.log(regions);
        
        // Reorder regions for visual display
        const orderedRegions = [];
        for (let band = 0; band < layout.bands; band++) {
            for (let leaf = 0; leaf < layout.leaves; leaf++) {
                const regionNum = regions[0].regionNum - (regions[0].regionNum % (layout.leaves * layout.bands)) + leaf * layout.bands + band;
                const region = regions.find(r => r.regionNum === regionNum);
                if (region) {
                    orderedRegions.push(region);
                } else {
                    orderedRegions.push({ regionNum, icons: [] });
                }
            }
        }
        
        return `
            <div class="regions-container" style="
                justify-content: start; 
                align-items: start; 
                grid-template-columns: repeat(${layout.leaves}, ${layout.regionWidth}px); 
                grid-template-rows: repeat(${layout.bands}, ${layout.regionHeight}px); 
                gap: ${layout.regionGapV}px ${layout.regionGapH}px;
                margin: ${margin}px;
            ">
                ${orderedRegions.map(region => renderRegion(region)).join('')}
            </div>
        `;
    }
}

export function renderRegion(region) {
    const slots = [];
    for (let i = 0; i < 12; i++) {
        const icon = region.icons.find(ic => ic.position === i);
        slots.push(renderIcon(icon, region.regionNum, i));
    }
    
    return `<div class="region" data-region="${region.regionNum}">${slots.join('')}</div>`;
}

export function renderIcon(icon, region, position) {
    if (!icon) {
        return `<div class="icon-slot" data-region="${region}" data-position="${position}"></div>`;
    }
    
    const badgeHtml = (icon.showBadge && icon.badge) ? `<div class="icon-badge">${icon.badge}</div>` : '';
    let imageHtml = '';
    if (icon.type == 'folder') {
        const folderIcons = window.icons.filter(nicon => nicon.kfolder == icon.id).sort((a, b) => (a.region*12+a.position) - (b.region*12+b.position));
        let x = 0;
        imageHtml = '<div style="display: flex; flex-wrap: wrap;">';
        for (let i = 0; i < folderIcons.length; i++) {
            const micon = folderIcons[i];
            if (micon.kfolder === icon.id) {
                imageHtml += micon.image ? `<img src="${escapeHtml(micon.image)}" alt="${escapeHtml(micon.title)}">` : `<img src="/api/static/images/blank-app.svg" alt="${escapeHtml(micon.title)}">`;
                x++;
                if (x == 4) break;
            }
        }
        imageHtml += '</div>';
    } else {
        imageHtml = icon.image ? `<img src="${escapeHtml(icon.image)}" alt="${escapeHtml(icon.title)}">` : `<img src="/api/static/images/blank-app.svg" alt="${escapeHtml(icon.title)}">`;
    }
    const folderClass = icon.type === 'folder' ? 'folder' : '';
    
    return `
        <div class="icon-slot" data-region="${region}" data-position="${position}">
            <div class="appicon ${folderClass}" data-icon-id="${icon.id}" data-type="${icon.type}">
                ${imageHtml}
                ${badgeHtml}
            </div>
            <div class="icon-title">${escapeHtml(icon.title)}</div>
        </div>
    `;
}

export function renderPageIndicators(count, containerId) {
    const indicators = document.getElementById(containerId);
    indicators.innerHTML = '';
    
    if (count <= 1) return;
    
    for (let i = 0; i < count; i++) {
        const dot = document.createElement('div');
        dot.className = `page-indicator ${i === (containerId === 'pageIndicators' ? state.currentScreen : state.currentFolderScreen) ? 'active' : ''}`;
        dot.addEventListener('click', () => {
            if (containerId === 'pageIndicators') {
                nav.goToScreen(i);
            } else {
                nav.goToFolderScreen(i);
            }
        });
        indicators.appendChild(dot);
    }
}

export function renderPageIndicatorsWithTemp(actualCount, side, containerId = 'pageIndicators', currentIndex = null) {
    const indicators = document.getElementById(containerId);
    indicators.innerHTML = '';
    
    if (currentIndex === null) {
        currentIndex = containerId === 'pageIndicators' ? state.currentScreen : state.currentFolderScreen;
    }
    
    // Add + indicator on left if needed
    if (side === 'left') {
        const plus = document.createElement('div');
        plus.className = 'page-indicator temp-indicator';
        plus.textContent = '+';
        plus.style.fontWeight = 'bold';
        plus.style.fontSize = '14px';
        plus.style.width = '16px';
        plus.style.height = '16px';
        plus.style.display = 'flex';
        plus.style.alignItems = 'center';
        plus.style.justifyContent = 'center';
        indicators.appendChild(plus);
    }
    
    // Add regular dots
    for (let i = 0; i < actualCount; i++) {
        const dot = document.createElement('div');
        const screenIndex = side === 'left' ? i + 1 : i;
        dot.className = `page-indicator ${screenIndex === currentIndex ? 'active' : ''}`;
        dot.addEventListener('click', () => {
            if (containerId === 'pageIndicators') {
                nav.goToScreen(screenIndex);
            } else {
                nav.goToFolderScreen(screenIndex);
            }
        });
        indicators.appendChild(dot);
    }
    
    // Add + indicator on right if needed
    if (side === 'right') {
        const plus = document.createElement('div');
        plus.className = 'page-indicator temp-indicator';
        plus.textContent = '+';
        plus.style.fontWeight = 'bold';
        plus.style.fontSize = '14px';
        plus.style.width = '16px';
        plus.style.height = '16px';
        plus.style.display = 'flex';
        plus.style.alignItems = 'center';
        plus.style.justifyContent = 'center';
        indicators.appendChild(plus);
    }
}

export function updateFolderHeight() {
    if (!state.openFolder) return;
    
    const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
    const layout = calculateLayout();
    
    // Calculate ranks needed based on icon positions
    let maxRank = 0;
    folderIcons.forEach(icon => {
        const rank = Math.floor(icon.position / 4);
        if (rank > maxRank) maxRank = rank;
    });
    
    // Use new folder dimension calculation
    const dimensions = calculateFolderDimensions(layout, maxRank);
    
    // Update container and content dimensions
    const folderContainer = document.getElementById('folderContainer');
    const folderContent = document.querySelector('.folder-content');
    const screensContainer = document.getElementById('folderScreens');
    
    folderContainer.style.width = dimensions.width + 'px';
    folderContainer.style.height = dimensions.height + 'px';
    
    folderContent.style.width = dimensions.width + 'px';
    folderContent.style.height = dimensions.height + 'px';
    
    // Check for empty screens in folder
    const totalScreens = screensContainer.children.length;
    
    // Update all screen heights and check for empty screens
    Array.from(screensContainer.children).forEach((screenDiv, screenIndex) => {
        screenDiv.style.height = dimensions.height + 'px';
        
        // Check if this is an empty screen that should show delete option
        const isEmpty = isEmptyScreenForDeletion(screenIndex, totalScreens, folderIcons, true);
        
        if (isEmpty) {
            screenDiv.innerHTML = renderEmptyScreenIndicator(screenIndex, true);
        }
    });
    
    // Attach delete handlers for any new empty screen indicators
    attachEmptyScreenDeleteHandlers();
}
